import { Tooltip } from 'antd';
import classNames from 'classnames';
import parse from 'html-react-parser';
import React, {
  CSSProperties,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { combineUserName } from '@helpers/combineUserName';
import UserAvatar from '@components/UserAvatar';
import {
  AssetVersionCommentGroupMentionDto,
  AssetVersionCommentMentionDto,
  AssetVersionCommentTimecodeDto
} from '@api/Api';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { LgWidth, MdWidth } from '@components/Responsive';
import { useResponsive } from '@hooks/useResponsive';
import { formatDuration } from '@helpers/formatDuration';
import commentShortTime from '@helpers/commentShortTime';

function MarkTooltipContent({
  user,
  comment,
  createdAt,
  timeCode,
  mentions,
  groupMentions,
  onReadMore
}: {
  user: any;
  comment: string;
  createdAt: string;
  timeCode?: AssetVersionCommentTimecodeDto | null;
  mentions: AssetVersionCommentMentionDto[];
  groupMentions: AssetVersionCommentGroupMentionDto[];
  onReadMore: () => void;
}) {
  const [isShowButton, setIsShowButton] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  const prepareHtml = useCallback(() => {
    let html = comment;
    mentions.forEach((el) => {
      html = html.replaceAll(
        `{{${el.id}}}`,
        `<span class="b-suggestion-user">@${combineUserName(el)}</span>`
      );
    });
    groupMentions.forEach((el) => {
      html = html.replaceAll(
        `{{${el.id}}}`,
        `<span class="b-suggestion-user">@${el.name}</span>`
      );
    });
    html = html.replaceAll(
      /{{(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})}}/g,
      `<span class="b-suggestion-user">@Deleted User</span>`
    );
    return parse(html);
  }, [comment, mentions, groupMentions]);

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (!el) {
      setIsShowButton(false);
      return;
    }
    setIsShowButton(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    checkTruncation();
  }, [checkTruncation, comment, timeCode]);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation]);

  const timecodeLabel =
    timeCode?.fromSeconds !== undefined
      ? formatDuration(timeCode.fromSeconds)
      : null;

  return (
    <div className="mark_tooltip">
      <div className="mark_tooltip__header">
        <UserAvatar
          isActive={true}
          size={24}
          src={user?.picture.url || ''}
          userEmail={user?.email || ''}
          userName={user?.name || ''}
        />
        <span className="mark_tooltip__name">
          {user ? combineUserName(user) : 'Deleted User'}
        </span>
        <span className="mark_tooltip__posted">
          {commentShortTime(createdAt)}
        </span>
      </div>
      <div ref={textRef} className="mark_tooltip__content">
        {timecodeLabel && (
          <span className="mark_tooltip__timecode">{timecodeLabel}</span>
        )}
        {prepareHtml()}
      </div>
      {isShowButton && (
        <button
          type="button"
          className="mark_tooltip__read-more"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onReadMore();
          }}
        >
          Read more
        </button>
      )}
    </div>
  );
}

function Mark({
  id,
  user,
  color,
  comment,
  createdAt,
  timeCode,
  isExpand,
  isRange,
  mentions,
  groupMentions,
  setSelectedCollapse
}: {
  id: string;
  color: string;
  user: any;
  comment: string;
  createdAt: string;
  timeCode?: AssetVersionCommentTimecodeDto | null;
  isExpand: boolean;
  isRange: boolean;
  mentions: AssetVersionCommentMentionDto[];
  groupMentions: AssetVersionCommentGroupMentionDto[];
  setSelectedCollapse?: (collapse: string | null) => void;
}) {
  const { isMdWidth } = useResponsive();
  const { selectedCommentId, canvasMode, selectComment, setCanvasMode } =
    useAssetViewerState();

  if (isExpand || canvasMode === 'edit') return null;

  const onSelectComment = () => {
    setCanvasMode('view');
    selectComment(id);
  };

  const onReadMore = () => {
    setSelectedCollapse?.('comments');
    onSelectComment();
  };

  return (
    <span
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelectComment();
      }}
      className={classNames(
        selectedCommentId === id ? (isRange ? 'for_range' : 'for_pin') : '',
        'mark'
      )}
    >
      <Tooltip
        placement="top"
        overlayClassName="selected_mark_overlay"
        overlayStyle={
          {
            '--mark-border-color': color
          } as CSSProperties
        }
        overlayInnerStyle={{ maxWidth: 300 }}
        getPopupContainer={() =>
          document.querySelector('.video-js') as HTMLElement
        }
        title={
          <MarkTooltipContent
            user={user}
            comment={comment}
            createdAt={createdAt}
            timeCode={timeCode}
            mentions={mentions}
            groupMentions={groupMentions}
            onReadMore={onReadMore}
          />
        }
        trigger={isMdWidth ? [] : ['hover']}
      >
        <MdWidth>
          <svg
            width="9"
            height="9"
            viewBox="0 0 9 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={
              selectedCommentId && selectedCommentId !== id
                ? { opacity: 0.3 }
                : selectedCommentId === id
                ? { transform: 'scale(1.5)' }
                : {}
            }
          >
            <path
              d="M7.76522 0L1.00129 0C0.257907 0 -0.225588 0.782314 0.106863 1.44722L3.48883 8.21115C3.85735 8.94819 4.90916 8.94819 5.27768 8.21115L8.65965 1.44722C8.9921 0.782314 8.5086 0 7.76522 0Z"
              fill={color}
            />
          </svg>
        </MdWidth>
        <LgWidth>
          <svg
            width="14"
            height="13"
            style={
              selectedCommentId && selectedCommentId !== id
                ? { opacity: 0.3 }
                : selectedCommentId === id
                ? { transform: 'scale(1.5)' }
                : {}
            }
            viewBox="0 0 14 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.61803 11.999L10.382 11.999C11.8687 11.999 12.8357 10.4344 12.1708 9.10459L8.78885 2.34066C8.05181 0.866567 5.94819 0.866568 5.21115 2.34066L6.10557 2.78788L5.21115 2.34066L1.82918 9.1046C1.16428 10.4344 2.13127 11.999 3.61803 11.999Z"
              fill={color}
              stroke="#292B2E"
              strokeWidth="2"
            />
          </svg>
        </LgWidth>
      </Tooltip>
    </span>
  );
}

export default Mark;
